/**
 * DeleteButton — Standardized destructive/remove action button
 *
 * Replaces inconsistent 🗑️ emoji, ×, "Remove", and text-only delete buttons
 * throughout the application with a single, theme-aware, accessible component.
 *
 * Modes:
 *   - icon-only (default): Trash icon in a square hit target
 *   - labeled: Trash icon + text label, auto-width
 *
 * Sizes: sm (24px), md (32px), lg (40px)
 *
 * Follows CLAUDE.md conventions:
 *   - useAppTranslation for i18n
 *   - CSS custom property tokens only
 *   - CSS logical properties for RTL
 */

import React from 'react';
import { Trash2 } from 'lucide-react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import styles from './DeleteButton.module.css';

type DeleteButtonSize = 'sm' | 'md' | 'lg';

interface DeleteButtonProps {
  /** Click handler */
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Visual size. Default: 'md' */
  size?: DeleteButtonSize;
  /** Text label shown next to icon. When set, button auto-sizes to content. */
  label?: string;
  /** Override the default aria-label. Localized "Delete" is used if omitted. */
  ariaLabel?: string;
  /** Additional CSS class */
  className?: string;
  /** Optional title tooltip */
  title?: string;
  /** Disabled state */
  disabled?: boolean;
}

const ICON_SIZE: Record<DeleteButtonSize, number> = {
  sm: 13,
  md: 16,
  lg: 20,
};

const STROKE_WIDTH: Record<DeleteButtonSize, number> = {
  sm: 2.2,
  md: 2,
  lg: 1.8,
};

const DeleteButton: React.FC<DeleteButtonProps> = ({
  onClick,
  size = 'md',
  label,
  ariaLabel,
  className,
  title,
  disabled = false,
}) => {
  const { t } = useAppTranslation('common');

  const classes = [
    styles.deleteBtn,
    styles[size],
    label ? styles.labeled : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      aria-label={ariaLabel || label || t('button.delete', { defaultValue: 'Delete' })}
      title={title}
      disabled={disabled}
    >
      <Trash2 size={ICON_SIZE[size]} strokeWidth={STROKE_WIDTH[size]} />
      {label && <span>{label}</span>}
    </button>
  );
};

export default DeleteButton;
